import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { 
  Cookie, 
  Shield, 
  X, 
  ChevronDown, 
  ChevronUp,
  Globe,
  Lock,
  BarChart3,
  Mail,
  Mic,
  Users,
  Sparkles
} from "lucide-react";
import { cn } from "@/lib/utils";

const API_URL = process.env.REACT_APP_API_URL;

// Regional compliance info
const REGION_CONFIG = {
  eu: {
    name: "European Union",
    law: "GDPR",
    strictMode: true,
    defaultConsent: false,
    message: "We use cookies to enhance your experience. Under GDPR, we need your explicit consent for non-essential cookies.",
    learnMoreUrl: "/privacy#gdpr"
  },
  ca: {
    name: "Canada", 
    law: "PIPEDA",
    strictMode: true,
    defaultConsent: false,
    message: "We value your privacy. Under PIPEDA, we're committed to protecting your personal information.",
    learnMoreUrl: "/privacy#pipeda"
  },
  us: {
    name: "United States",
    law: "CCPA",
    strictMode: false,
    defaultConsent: true,
    message: "We use cookies to improve your experience. California residents have additional rights under CCPA.",
    learnMoreUrl: "/privacy#ccpa"
  },
  ae: {
    name: "United Arab Emirates",
    law: "PDPL",
    strictMode: true,
    defaultConsent: false,
    message: "We respect your privacy rights under UAE's Personal Data Protection Law.",
    learnMoreUrl: "/privacy#pdpl"
  },
  global: {
    name: "Global",
    law: "Privacy Policy",
    strictMode: false,
    defaultConsent: true,
    message: "We use cookies to enhance your browsing experience and analyze site traffic.",
    learnMoreUrl: "/privacy"
  }
};

const CONSENT_CATEGORIES = [
  {
    id: "essential",
    name: "Essential",
    description: "Required for the website to function. Cannot be disabled.",
    icon: Lock,
    required: true
  },
  {
    id: "analytics",
    name: "Analytics",
    description: "Help us understand how visitors interact with our website.",
    icon: BarChart3,
    required: false
  },
  {
    id: "marketing",
    name: "Marketing",
    description: "Used to deliver personalized advertisements and promotions.",
    icon: Mail,
    required: false
  },
  {
    id: "call_recording",
    name: "Call Recording",
    description: "Allow AI agents to record calls for quality and training purposes.",
    icon: Mic,
    required: false
  },
  {
    id: "personalization",
    name: "Personalization",
    description: "Enable AI to personalize your experience based on your usage.",
    icon: Sparkles,
    required: false
  },
  {
    id: "data_sharing",
    name: "Third-Party Sharing",
    description: "Share data with partners to enhance services.",
    icon: Users,
    required: false
  }
];

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [region, setRegion] = useState("global");
  const [consents, setConsents] = useState({
    essential: true,
    analytics: false,
    marketing: false,
    call_recording: false,
    personalization: false,
    data_sharing: false
  });
  const [saving, setSaving] = useState(false);

  const detectRegion = async () => {
    try {
      // Try to detect region from browser timezone/locale first
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Simple timezone-based detection
      if (timezone.includes("Europe") || timezone.includes("London") || timezone.includes("Paris") || timezone.includes("Berlin")) {
        setRegion("eu");
        setConsents(prev => ({ ...prev, analytics: false, marketing: false }));
      } else if (timezone.includes("America/Toronto") || timezone.includes("America/Vancouver") || timezone.includes("America/Montreal")) {
        setRegion("ca");
        setConsents(prev => ({ ...prev, analytics: false, marketing: false }));
      } else if (timezone.includes("America")) {
        setRegion("us");
        setConsents(prev => ({ ...prev, analytics: true, marketing: true }));
      } else if (timezone.includes("Dubai") || timezone.includes("Asia/Dubai")) {
        setRegion("ae");
        setConsents(prev => ({ ...prev, analytics: false, marketing: false }));
      } else {
        setRegion("global");
      }

      // Also try IP-based detection via backend
      try {
        const res = await fetch(`${API_URL}/api/privacy/detect-region`, { 
          method: "GET",
          headers: { "Accept": "application/json" }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.region && REGION_CONFIG[data.region]) {
            setRegion(data.region);
            const config = REGION_CONFIG[data.region];
            if (config.strictMode) {
              setConsents(prev => ({
                ...prev,
                analytics: false,
                marketing: false,
                personalization: false,
                data_sharing: false
              }));
            }
          }
        }
      } catch (e) {
        // IP detection failed, use timezone detection
      }
    } catch (e) {
      setRegion("global");
    }
  };

  useEffect(() => {
    // Check if consent already given
    const savedConsent = localStorage.getItem("speakr_cookie_consent");
    if (savedConsent) {
      return; // Don't show banner if already consented
    }

    // Detect region
    detectRegion();
    
    // Show banner after a short delay
    const timer = setTimeout(() => setVisible(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleAcceptAll = async () => {
    const allConsents = {
      essential: true,
      analytics: true,
      marketing: true,
      call_recording: true,
      personalization: true,
      data_sharing: true
    };
    await saveConsents(allConsents);
  };

  const handleRejectNonEssential = async () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false,
      call_recording: false,
      personalization: false,
      data_sharing: false
    };
    await saveConsents(essentialOnly);
  };

  const handleSavePreferences = async () => {
    await saveConsents(consents);
  };

  const saveConsents = async (consentData) => {
    setSaving(true);
    
    // Save to localStorage
    localStorage.setItem("speakr_cookie_consent", JSON.stringify({
      consents: consentData,
      region: region,
      timestamp: new Date().toISOString(),
      version: "1.0"
    }));

    // Try to save to backend if user is identified
    const userId = localStorage.getItem("speakr_user_id");
    if (userId) {
      try {
        await fetch(`${API_URL}/api/privacy/consents/${userId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(consentData)
        });
      } catch (e) {
        // Silent fail for anonymous users
      }
    }

    setSaving(false);
    setVisible(false);
  };

  const config = REGION_CONFIG[region];

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9998]" />
      
      {/* Banner */}
      <div 
        className={cn(
          "fixed bottom-0 left-0 right-0 z-[9999] transition-all duration-500 ease-out",
          visible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
        )}
        data-testid="cookie-consent-banner"
      >
        <div className="max-w-4xl mx-auto p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="p-6 pb-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <Cookie className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      Privacy Preferences
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 text-xs font-medium">
                        <Globe className="w-3 h-3" />
                        {config.law}
                      </span>
                    </h3>
                    <p className="text-sm text-slate-400 mt-1">{config.message}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setVisible(false)}
                  className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
            </div>

            {/* Expandable preferences */}
            {expanded && (
              <div className="px-6 pb-4 border-t border-slate-800">
                <div className="pt-4 space-y-3">
                  {CONSENT_CATEGORIES.map((category) => {
                    const Icon = category.icon;
                    const isEnabled = consents[category.id];
                    
                    return (
                      <div 
                        key={category.id}
                        className={cn(
                          "flex items-center justify-between p-3 rounded-lg transition-colors",
                          category.required 
                            ? "bg-slate-800/50" 
                            : isEnabled 
                              ? "bg-emerald-900/20 border border-emerald-800/50" 
                              : "bg-slate-800/30"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-8 h-8 rounded-lg flex items-center justify-center",
                            isEnabled ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-700 text-slate-400"
                          )}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div>
                            <Label className="text-sm font-medium text-white flex items-center gap-2">
                              {category.name}
                              {category.required && (
                                <span className="text-xs text-slate-500">(Required)</span>
                              )}
                            </Label>
                            <p className="text-xs text-slate-500 mt-0.5">{category.description}</p>
                          </div>
                        </div>
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={(v) => !category.required && setConsents(prev => ({ ...prev, [category.id]: v }))}
                          disabled={category.required}
                          data-testid={`consent-${category.id}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="p-6 pt-4 bg-slate-800/50 flex flex-col sm:flex-row items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpanded(!expanded)}
                className="text-slate-400 hover:text-white order-last sm:order-first"
                data-testid="customize-cookies-btn"
              >
                {expanded ? (
                  <>
                    <ChevronUp className="w-4 h-4 mr-1" />
                    Hide Options
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-1" />
                    Customize
                  </>
                )}
              </Button>
              
              <div className="flex-1" />
              
              <div className="flex items-center gap-2 w-full sm:w-auto">
                {config.strictMode && (
                  <Button
                    variant="outline"
                    onClick={handleRejectNonEssential}
                    disabled={saving}
                    className="flex-1 sm:flex-none border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
                    data-testid="reject-cookies-btn"
                  >
                    Essential Only
                  </Button>
                )}
                
                {expanded ? (
                  <Button
                    onClick={handleSavePreferences}
                    disabled={saving}
                    className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="save-cookies-btn"
                  >
                    {saving ? "Saving..." : "Save Preferences"}
                  </Button>
                ) : (
                  <Button
                    onClick={handleAcceptAll}
                    disabled={saving}
                    className="flex-1 sm:flex-none bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                    data-testid="accept-cookies-btn"
                  >
                    {saving ? "Saving..." : "Accept All"}
                  </Button>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-3 bg-slate-800/30 border-t border-slate-800 flex items-center justify-between text-xs text-slate-500">
              <span>
                Detected region: {config.name} • {config.law} compliant
              </span>
              <a 
                href={config.learnMoreUrl} 
                className="text-blue-400 hover:text-blue-300 underline"
              >
                Privacy Policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
